import { Component, OnDestroy, OnInit } from "@angular/core";
import { FormControl } from "@angular/forms";
import { debounceTime, distinctUntilChanged, forkJoin, Subject, takeUntil } from "rxjs";
import { LogtoUser } from "../model/LogtoUser";
import { LogtoUsersService } from "../services/logto-users.service";
import { RolesService } from "../services/roles.service";
import { RoleAssignment } from "../model/RoleAssignment";

interface SuperadminRow {
  assignment: RoleAssignment;
  user: LogtoUser | undefined;
}

@Component({
  selector: "app-admin-management",
  templateUrl: "./admin-management.component.html",
  styleUrls: ["./admin-management.component.css"],
})
export class AdminManagementComponent implements OnInit, OnDestroy {
  searchControl = new FormControl("");
  users: LogtoUser[] = [];
  selectedUser: LogtoUser | null = null;
  superadmins: SuperadminRow[] = [];
  loading = false;
  saving = false;

  private allUsers: LogtoUser[] = [];
  private destroy$ = new Subject<void>();

  constructor(
    private logtoUsersService: LogtoUsersService,
    private rolesService: RolesService,
  ) {}

  ngOnInit() {
    this.loading = true;
    forkJoin({
      users: this.logtoUsersService.getUsers(),
      superadmins: this.rolesService.listSuperadmins(),
    }).subscribe({
      next: ({ users, superadmins }) => {
        this.allUsers = users;
        this.buildRows(superadmins);
        this.filterUsers("");
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((value) => this.filterUsers(value ?? ""));
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildRows(assignments: RoleAssignment[]) {
    this.superadmins = assignments.map((a) => ({
      assignment: a,
      user: this.allUsers.find((u) => u.id === a.logtoUserId),
    }));
  }

  private filterUsers(query: string) {
    const ids = new Set(this.superadmins.map((s) => s.assignment.logtoUserId));
    const available = this.allUsers.filter((u) => !ids.has(u.id));
    const q = query.trim().toLowerCase();
    this.users = !q
      ? available
      : available.filter(
          (u) =>
            u.primaryEmail?.toLowerCase().includes(q) ||
            u.name?.toLowerCase().includes(q) ||
            u.username?.toLowerCase().includes(q),
        );
  }

  onSelect(user: LogtoUser) {
    this.selectedUser = user;
  }

  addSuperadmin() {
    if (!this.selectedUser) {
      return;
    }
    const user = this.selectedUser;
    this.saving = true;
    this.rolesService
      .assign({
        logtoUserId: user.id,
        role: "SUPERADMIN",
        organizationId: null,
        facilityId: null,
      })
      .subscribe({
        next: (created) => {
          this.superadmins = [...this.superadmins, { assignment: created, user }];
          this.selectedUser = null;
          this.searchControl.setValue("");
          this.filterUsers("");
          this.saving = false;
        },
        error: () => {
          this.saving = false;
        },
      });
  }

  displayFn(user: LogtoUser | null): string {
    return user?.name || user?.primaryEmail || user?.username || "";
  }

  getInitials(user: LogtoUser | undefined): string {
    if (!user) {
      return "?";
    }
    if (user.name) {
      return user.name
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
    }
    return (user.primaryEmail?.[0] || user.username?.[0] || "?").toUpperCase();
  }
}
